import { Link } from "@remix-run/react";
import { CREDENTIAL_TYPE } from "@repo/shared";
import { useTranslation } from "react-i18next";

import { CertificateCard } from "~/modules/Profile/Certificates/CertificateCard";
import { SAMPLE_CREDENTIAL } from "~/modules/Profile/Certificates/sampleCredential";

import { TornEdge } from "../CuadernoVivo/TornEdge";

/**
 * La credencial verificable. Replanteo 2026-07-31: antes eran cuatro features con
 * título Y descripción (≈120 palabras) al lado del ejemplo — el texto le ganaba al
 * objeto, y nadie lee cuatro párrafos para entender una sola idea.
 *
 * Ahora la idea es una ("se comprueba contra nuestros registros"), las pruebas son tres
 * chips de una línea, y el protagonista es el objeto mismo: es lo único de esta sección
 * que no se puede contar con palabras. En mobile va primero.
 *
 * Es copy GENERAL de la home, no de un curso: por eso nombra a las dos credenciales
 * (constancia en los gratis, certificado en los pagos) en vez de prometer certificado a
 * secas. La promesa por curso se decide con `credentialType` en la ficha.
 */
/**
 * El papel del que está hecha cada sección vecina. El borde rasgado NO es un adorno con
 * color propio: es la hoja de al lado rompiéndose, así que tiene que pintarse EXACTAMENTE
 * del color de esa hoja o se ve como una mancha pegada encima.
 */
const PAPEL = {
  white: "rgb(var(--landing-white))",
  cream: "rgb(var(--landing-cream))",
  paper: "rgb(var(--landing-paper))",
} as const;

type Papel = keyof typeof PAPEL;

type CertificateSectionProps = {
  /** De qué está hecha la sección que va JUSTO ARRIBA en la página. */
  above?: Papel;
  /** De qué está hecha la sección que va JUSTO ABAJO. */
  below?: Papel;
};

export function CertificateSection({ above = "white", below = "cream" }: CertificateSectionProps) {
  const { t } = useTranslation();

  return (
    // La banda OSCURA de la home. Es la misma que traía "Rutas de aprendizaje", que dejó
    // este lugar: se migró el tratamiento, no sólo el contenido.
    //
    // Por qué el degradado tiene 1px del color vecino arriba y abajo: la caja cae en
    // coordenada fraccionaria y con devicePixelRatio no entero (Windows al 125%, zoom del
    // navegador) esa fila se mezcla con lo de al lado y dibuja una línea horizontal de
    // lado a lado. Arrancando el oscuro 1px adentro, esa mezcla es vecino con vecino:
    // invisible en todos los DPR.
    //
    // ⚠️ El color de las costuras VIENE POR PROP y lo declara la página, porque el dato es
    // "qué hay arriba y qué hay abajo" y eso sólo lo sabe quien ordena las secciones.
    // Antes estaba fijo en `--landing-white` con un comentario que decía "los DOS vecinos
    // usan bg-landing-white". Dejó de ser cierto cuando la home se reordenó y abajo quedó
    // `QuotesSection`, que es crema: el rasgado de abajo siguió blanco sobre crema y se
    // veía como un recorte mal pegado. Medido sobre el sitio, la diferencia era de 27
    // puntos de canal en claro y 13 en oscuro. Si se vuelve a mover esta sección, hay que
    // actualizar `above` / `below` en la página — y el tipo no deja inventar un valor.
    <section className="landing-grain relative overflow-hidden px-5 pb-[104px] pt-[104px] sm:px-8 sm:pt-[120px]">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          // Los tonos de la banda son variables porque en modo oscuro tienen que bajar:
          // con los valores de claro, el borde inferior quedaba casi del mismo color que
          // la sección vecina y el corte con el papel rasgado desaparecía.
          background: `linear-gradient(180deg, ${PAPEL[above]} 0 1px, rgb(var(--landing-band-top)) 1px, rgb(var(--landing-band-top)) 62%, rgb(var(--landing-band-bottom)) calc(100% - 1px), ${PAPEL[below]} calc(100% - 1px))`,
        }}
      />
      <TornEdge color={PAPEL[above]} className="top-0" />
      <TornEdge color={PAPEL[below]} className="bottom-0" flip />
      <div className="relative z-10 mx-auto grid max-w-[1100px] items-center gap-10 md:grid-cols-[0.9fr_1.1fr] md:gap-14">
        <div>
          <h2 className="font-display text-balance text-[clamp(24px,3.2vw,34px)] font-extrabold leading-tight tracking-tight text-landing-on-dark">
            {t("landing.launch.cert.title")}
          </h2>
          <p className="mt-3 max-w-[46ch] text-[15.5px] leading-relaxed text-[rgba(255,255,255,0.72)]">
            {t("landing.launch.cert.lead")}
          </p>
          <Link
            to="/certificado"
            data-cuelume-hover="tick"
            data-cuelume-press
            className="mt-6 inline-flex items-center gap-1.5 text-[14.5px] font-bold text-[#7ecfd8] underline decoration-[#7ecfd8]/40 underline-offset-4 transition-colors hover:decoration-[#7ecfd8]"
          >
            {t("landing.launch.cert.seeExample")} →
          </Link>
        </div>

        {/* El objeto. En mobile va PRIMERO: es la prueba, no la ilustración.

            Es la MISMA tarjeta que recibe el alumno al terminar un curso (y que copia el
            PDF), no un dibujo aparte: antes acá había un cuarto diseño —cuaderno, nombre
            manuscrito, código en verde agua— que no se parecía a nada de lo que la persona
            se lleva. Datos de muestra y marca de agua EJEMPLO, porque una captura de esta
            sección circula suelta. La columna mide ~520px aunque la pantalla sea ancha: la
            tarjeta lo mide ella misma (container query) y se apila sola. Es papel claro
            también en modo oscuro, así que sobre esta banda se lee igual. */}
        <Link
          to="/certificado"
          aria-label="Ver el ejemplo de certificado"
          data-cuelume-press
          className="mx-auto block w-full max-w-[520px] transition-transform duration-300 hover:-translate-y-1 max-md:order-first"
        >
          <CertificateCard
            variant="example"
            {...SAMPLE_CREDENTIAL}
            type={CREDENTIAL_TYPE.CERTIFICADO}
          />
        </Link>
      </div>
    </section>
  );
}
